<template>
  <div v-if="visible" class="lead-receipts">
    <div class="lr-head">
      <div>
        <h3 class="mb-1 text-uppercase">{{ $t('settings.forms.leads.title') }}</h3>
        <p class="lr-muted mb-0">{{ $t('settings.forms.leads.intro') }}</p>
      </div>
      <div class="lr-count" data-test="month-count">
        <span class="lr-count-number">{{ monthCount }}</span>
        <span class="lr-count-label">{{ $t('settings.forms.leads.thisMonth') }}</span>
      </div>
    </div>

    <p v-if="!enabled" class="lr-note">{{ $t('settings.forms.leads.notEnabled') }}</p>
    <p v-if="error" class="lr-error" role="alert">{{ error }}</p>

    <div class="lr-toolbar">
      <div class="lr-filters">
        <b-form-checkbox v-model="allForms" switch @change="load">
          {{ $t('settings.forms.leads.allForms') }}
        </b-form-checkbox>
        <b-form-checkbox v-model="onlyFailed" switch @change="load">
          {{ $t('settings.forms.leads.onlyFailed') }}
        </b-form-checkbox>
      </div>
      <span class="lr-muted">{{ $t('settings.forms.leads.total', { n: total }) }}</span>
    </div>

    <ul v-if="leads.length" class="lr-list">
      <li v-for="lead in leads" :key="lead.lead_id" class="lr-row">
        <button type="button" class="lr-row-main" @click="openDetail(lead)">
          <span class="lr-time">{{ formatDate(lead.created_at) }}</span>
          <span class="lr-contact">
            <span class="lr-contact-email">{{ lead.email || $t('settings.forms.leads.noEmail') }}</span>
            <span v-if="allForms || source(lead) !== 'form'" class="lr-contact-source">
              {{ source(lead) === 'form' ? lead.form_name : $t(`settings.forms.leads.source.${source(lead)}`) }}
            </span>
          </span>
          <span class="lr-chips">
            <span
                v-for="chip in summary(lead)"
                :key="chip.channel"
                :class="['lr-chip', `lr-chip--${chip.status}`]"
            >
              <b-icon :icon="icon(chip.status)" aria-hidden="true" scale=".9"></b-icon>
              {{ $t(`settings.forms.leads.channel.${chip.channel}`) }}
              <span class="lr-chip-status">{{ $t(`settings.forms.leads.status.${chip.status}`) }}</span>
              <span v-if="chip.total > 1" class="lr-chip-n">{{ chip.sent }}/{{ chip.total }}</span>
            </span>
            <span v-if="!summary(lead).length" class="lr-muted">{{ $t('settings.forms.leads.noChannels') }}</span>
          </span>
          <b-icon icon="chevron-right" aria-hidden="true" class="lr-chevron"></b-icon>
        </button>
      </li>
    </ul>
    <p v-else-if="enabled && !loading" class="lr-empty">{{ $t('settings.forms.leads.empty') }}</p>

    <b-modal
        :id="modalId"
        size="lg"
        centered
        hide-footer
        :title="detail ? formatDate(detail.created_at) : ''"
        content-class="lr-modal"
    >
      <div v-if="detail">
        <dl class="lr-facts">
          <dt>{{ $t('settings.forms.leads.contact') }}</dt>
          <dd>{{ detail.email || $t('settings.forms.leads.noEmail') }}</dd>
          <dt>{{ $t('settings.forms.leads.origin') }}</dt>
          <dd>{{ source(detail) === 'form' ? detail.form_name : $t(`settings.forms.leads.source.${source(detail)}`) }}</dd>
          <dt>{{ $t('settings.forms.leads.page') }}</dt>
          <dd>{{ detail.page_name || detail.page_url || '–' }}</dd>
          <dt v-if="utmText">{{ $t('settings.forms.leads.campaign') }}</dt>
          <dd v-if="utmText">{{ utmText }}</dd>
          <dt>{{ $t('settings.forms.leads.consent') }}</dt>
          <dd>{{ consentText || $t('settings.forms.leads.noConsent') }}</dd>
        </dl>

        <h4 class="lr-sub">{{ $t('settings.forms.leads.deliveries') }}</h4>
        <ul class="lr-deliveries">
          <li v-for="d in detail.deliveries" :key="d.delivery_id" :class="['lr-delivery', `lr-delivery--${d.status}`]">
            <div class="lr-delivery-top">
              <b-icon :icon="icon(d.status)" aria-hidden="true"></b-icon>
              <strong>{{ $t(`settings.forms.leads.channel.${d.channel}`) }}</strong>
              <span>{{ $t(`settings.forms.leads.status.${d.status}`) }}</span>
              <b-button
                  v-if="resendable(d)"
                  size="sm"
                  class="green-button lr-resend"
                  :disabled="busy === d.delivery_id"
                  @click="resend(d)"
              >
                {{ $t('settings.forms.leads.resend') }}
              </b-button>
            </div>
            <div class="lr-delivery-target">{{ d.target }}</div>
            <div class="lr-delivery-times lr-muted">
              <span v-if="d.sent_at">{{ $t('settings.forms.leads.sentAt', { t: formatDate(d.sent_at) }) }}</span>
              <span v-else-if="d.status === 'pending'">
                {{ $t('settings.forms.leads.nextAt', { t: formatDate(d.next_attempt_at) }) }}
              </span>
              <span>{{ $tc('settings.forms.leads.attempts', d.attempts, { n: d.attempts }) }}</span>
            </div>
            <div v-if="d.error_text && d.status !== 'sent'" class="lr-delivery-error">{{ d.error_text }}</div>
            <details v-if="isSysAdmin && d.last_error && d.status !== 'sent'" class="lr-raw">
              <summary>{{ $t('settings.forms.leads.technical') }}</summary>
              <code>{{ d.last_error }}</code>
            </details>
          </li>
        </ul>

        <h4 v-if="detail.fields && detail.fields.length" class="lr-sub">{{ $t('settings.forms.leads.answers') }}</h4>
        <dl v-if="detail.fields && detail.fields.length" class="lr-facts">
          <template v-for="f in detail.fields">
            <dt :key="`l-${f.id}`">{{ f.label }}</dt>
            <dd :key="`v-${f.id}`">{{ f.value }}</dd>
          </template>
        </dl>
      </div>
    </b-modal>

    <div class="lr-cards">
      <section class="lr-card">
        <h4 class="lr-card-title">{{ $t('settings.forms.leads.mailsTitle') }}</h4>
        <b-form-checkbox v-model="settings.confirm_enabled" switch>
          {{ $t('settings.forms.leads.confirmOn') }}
        </b-form-checkbox>
        <div v-if="settings.confirm_enabled" class="lr-fields">
          <b-form-input v-model="settings.confirm_subject" :placeholder="$t('settings.forms.leads.confirmSubject')" maxlength="200" />
          <b-form-textarea v-model="settings.confirm_body" :placeholder="$t('settings.forms.leads.confirmBody')" rows="4" maxlength="5000" />
        </div>
        <b-form-checkbox v-model="settings.follow_up_enabled" switch class="mt-3">
          {{ $t('settings.forms.leads.followUpOn') }}
        </b-form-checkbox>
        <div v-if="settings.follow_up_enabled" class="lr-fields">
          <label class="lr-label" for="lr-consent">{{ $t('settings.forms.leads.followUpConsent') }}</label>
          <b-form-select id="lr-consent" v-model="settings.follow_up_consent_block_id" :options="consentOptions" />
          <b-form-input v-model="settings.follow_up_subject" :placeholder="$t('settings.forms.leads.followUpSubject')" maxlength="200" />
          <b-form-textarea v-model="settings.follow_up_body" :placeholder="$t('settings.forms.leads.followUpBody')" rows="4" maxlength="5000" />
          <p class="lr-muted mb-0">{{ $t('settings.forms.leads.followUpHint') }}</p>
        </div>
        <div v-if="settings.confirm_enabled || settings.follow_up_enabled" class="lr-fields">
          <b-form-input v-model="settings.reply_to" type="email" :placeholder="$t('settings.forms.leads.replyTo')" maxlength="200" />
          <p class="lr-muted mb-0">{{ $t('settings.forms.leads.placeholders', { list: placeholderList }) }}</p>
        </div>
        <div class="lr-actions">
          <span v-if="settingsSaved" class="lr-ok">{{ $t('settings.forms.leads.saved') }}</span>
          <b-button class="green-button" :disabled="savingSettings" @click="saveSettings">
            {{ $t('buttons.save') }}
          </b-button>
        </div>
      </section>

      <section class="lr-card">
        <h4 class="lr-card-title">{{ $t('settings.forms.leads.webhooksTitle') }}</h4>
        <p class="lr-muted">{{ $t('settings.forms.leads.webhooksIntro') }}</p>
        <ul v-if="webhooks.length" class="lr-hooks">
          <li v-for="h in webhooks" :key="h.webhook_id" class="lr-hook">
            <div class="lr-hook-main">
              <span :class="['lr-dot', h.active ? 'lr-dot--on' : 'lr-dot--off']" aria-hidden="true"></span>
              <span class="lr-hook-url">{{ h.url }}</span>
              <span v-if="h.description" class="lr-muted">{{ h.description }}</span>
              <span v-if="!h.active" class="lr-muted">{{ $t('settings.forms.leads.hookOff') }}</span>
            </div>
            <div class="lr-hook-actions">
              <span v-if="testResults[h.webhook_id]" :class="testResults[h.webhook_id].ok ? 'lr-ok' : 'lr-error-inline'">
                {{ testText(testResults[h.webhook_id]) }}
              </span>
              <b-button size="sm" class="grey-button" @click="testHook(h)">{{ $t('settings.forms.leads.test') }}</b-button>
              <b-button size="sm" class="grey-button" @click="editHook(h)">{{ $t('dropdowns.edit') }}</b-button>
              <b-button size="sm" class="grey-button" @click="deleteHook(h)">{{ $t('dropdowns.delete') }}</b-button>
            </div>
          </li>
        </ul>
        <div v-if="hookForm" class="lr-fields lr-hook-form">
          <b-form-input v-model="hookForm.url" type="url" placeholder="https://hooks.zapier.com/…" maxlength="2000" />
          <b-form-input v-model="hookForm.description" :placeholder="$t('settings.forms.leads.hookDescription')" maxlength="200" />
          <b-form-checkbox v-model="hookForm.active" switch>{{ $t('settings.forms.leads.hookActive') }}</b-form-checkbox>
          <div class="lr-secret">
            <b-form-input
                v-model="hookForm.secret"
                :placeholder="hookForm.webhookId ? $t('settings.forms.leads.secretKeep') : $t('settings.forms.leads.secret')"
                maxlength="200"
                autocomplete="off"
            />
            <b-button size="sm" class="grey-button" @click="makeSecret">{{ $t('settings.forms.leads.secretMake') }}</b-button>
          </div>
          <p class="lr-muted mb-0">{{ $t('settings.forms.leads.secretHint') }}</p>
          <div class="lr-actions">
            <b-button class="grey-button" @click="hookForm = null">{{ $t('buttons.cancel') }}</b-button>
            <b-button class="green-button" :disabled="savingHook" @click="saveHook">{{ $t('buttons.save') }}</b-button>
          </div>
        </div>
        <b-button v-else class="green-button-transparent border-0" @click="newHook">
          <b-icon icon="plus-circle-fill" class="mr-2" aria-hidden="true"></b-icon>
          {{ $t('settings.forms.leads.hookAdd') }}
        </b-button>
        <details class="lr-recipe">
          <summary>{{ $t('settings.forms.leads.verifyTitle') }}</summary>
          <p class="lr-muted">{{ $t('settings.forms.leads.verifyText') }}</p>
          <pre>expected = "sha256=" + hex(hmac_sha256(secret, timestamp + "." + rawBody))
compare(expected, header["X-Webmag-Signature"])   // constant time
reject if now - header["X-Webmag-Timestamp"] > 300 s</pre>
        </details>
      </section>
    </div>
  </div>
</template>

<script>
import LeadReceipts from '@/graphQlQueries/mutations/leads/leadReceipts';
import {
  channelSummary, statusIcon, canResend, generateSecret, consentBlocksOf, sourceKey,
} from '@/components/settings/forms/leadReceipts.helpers';

export default {
  name: 'LeadReceipts',
  props: {
    formId: { type: Number, required: true },
    formDoc: { type: Object, default: null },
    isSysAdmin: { type: Boolean, default: false },
  },
  data() {
    return {
      enabled: false,
      loaded: false,
      loading: false,
      error: null,
      leads: [],
      total: 0,
      monthCount: 0,
      onlyFailed: false,
      allForms: false,
      detail: null,
      busy: null,
      settings: {},
      savingSettings: false,
      settingsSaved: false,
      webhooks: [],
      hookForm: null,
      savingHook: false,
      testResults: {},
    };
  },
  computed: {
    placeholderList() {
      return ['{{form}}', '{{email}}'].join(', ');
    },
    // Customers of accounts without the switch never see the section; sysadmins always (support).
    visible() {
      return this.loaded && (this.enabled || this.isSysAdmin);
    },
    modalId() {
      return `lead-detail-${this.formId}`;
    },
    consentOptions() {
      return [
        { value: null, text: this.$t('settings.forms.leads.pickConsent') },
        ...consentBlocksOf(this.formDoc).map((b) => ({ value: b.id, text: b.label })),
      ];
    },
    utmText() {
      const utm = (this.detail && this.detail.utm) || {};
      return Object.keys(utm).map((k) => `${k.replace('utm_', '')}: ${utm[k]}`).join(' · ');
    },
    consentText() {
      return ((this.detail && this.detail.consent) || []).map((c) => c.label).join(' · ');
    },
  },
  watch: {
    formId: { immediate: true, handler: 'loadAll' },
  },
  methods: {
    summary: channelSummary,
    source: sourceKey,
    icon: statusIcon,
    resendable: canResend,
    async call(op, args) {
      const { data } = await this.$apollo.mutate({ mutation: LeadReceipts, variables: { op, args } });
      return data.leadReceipts.result;
    },
    message(err) {
      const gql = err && err.graphQLErrors && err.graphQLErrors[0];
      return (gql && gql.message) || this.$t('settings.forms.leads.failed');
    },
    async loadAll() {
      await this.load();
      if (!this.visible) return;
      try {
        const [settings, webhooks] = await Promise.all([
          this.call('settingsGet', { formId: this.formId }),
          this.call('webhooksList', { formId: this.formId }),
        ]);
        this.settings = { ...settings };
        this.webhooks = webhooks || [];
      } catch (err) {
        this.error = this.message(err);
      }
    },
    async load() {
      this.loading = true;
      this.error = null;
      try {
        const result = await this.call('list', { formId: this.formId, onlyFailed: this.onlyFailed, allForms: this.allForms });
        this.enabled = !!result.enabled;
        this.leads = result.leads || [];
        this.total = result.total || 0;
        this.monthCount = result.monthCount || 0;
      } catch (err) {
        // 403 for customers of accounts without the switch: the section stays hidden.
        this.enabled = false;
        if (this.isSysAdmin) this.error = this.message(err);
      } finally {
        this.loaded = true;
        this.loading = false;
      }
    },
    async openDetail(lead) {
      try {
        this.detail = await this.call('detail', { leadId: Number(lead.lead_id) });
        this.$bvModal.show(this.modalId);
      } catch (err) {
        this.error = this.message(err);
      }
    },
    async resend(delivery) {
      this.busy = delivery.delivery_id;
      try {
        await this.call('resend', { deliveryId: Number(delivery.delivery_id) });
        this.detail = await this.call('detail', { leadId: Number(this.detail.lead_id) });
        await this.load();
      } catch (err) {
        this.error = this.message(err);
      } finally {
        this.busy = null;
      }
    },
    async saveSettings() {
      this.savingSettings = true;
      this.settingsSaved = false;
      this.error = null;
      try {
        this.settings = { ...(await this.call('settingsSave', { ...this.settings, formId: this.formId })) };
        this.settingsSaved = true;
      } catch (err) {
        this.error = this.message(err);
      } finally {
        this.savingSettings = false;
      }
    },
    newHook() {
      this.hookForm = {
        webhookId: null, url: '', description: '', active: true, secret: generateSecret(window.crypto),
      };
    },
    editHook(h) {
      this.hookForm = {
        webhookId: h.webhook_id, url: h.url, description: h.description, active: h.active, secret: '',
      };
    },
    makeSecret() {
      this.hookForm.secret = generateSecret(window.crypto);
    },
    async saveHook() {
      this.savingHook = true;
      this.error = null;
      try {
        const args = {
          formId: this.formId,
          url: this.hookForm.url,
          description: this.hookForm.description,
          active: this.hookForm.active,
          secret: this.hookForm.secret || null,
        };
        if (this.hookForm.webhookId) args.webhookId = this.hookForm.webhookId;
        await this.call('webhookSave', args);
        this.hookForm = null;
        this.webhooks = await this.call('webhooksList', { formId: this.formId });
      } catch (err) {
        this.error = this.message(err);
      } finally {
        this.savingHook = false;
      }
    },
    async deleteHook(h) {
      const ok = await this.$bvModal.msgBoxConfirm(this.$t('settings.forms.leads.hookDeleteConfirm'), { centered: true });
      if (!ok) return;
      try {
        await this.call('webhookDelete', { webhookId: h.webhook_id });
        this.webhooks = await this.call('webhooksList', { formId: this.formId });
      } catch (err) {
        this.error = this.message(err);
      }
    },
    async testHook(h) {
      this.$set(this.testResults, h.webhook_id, { ok: true, pending: true });
      try {
        this.$set(this.testResults, h.webhook_id, await this.call('webhookTest', { webhookId: h.webhook_id }));
      } catch (err) {
        this.$set(this.testResults, h.webhook_id, { ok: false, error: this.message(err) });
      }
    },
    testText(r) {
      if (r.pending) return this.$t('settings.forms.leads.testing');
      if (r.ok) return this.$t('settings.forms.leads.testOk', { status: r.status });
      // Sysadmins also see the raw error behind the German text.
      return this.isSysAdmin && r.error_raw ? `${r.error} (${r.error_raw})` : r.error;
    },
    formatDate(value) {
      if (!value) return '–';
      return new Date(value).toLocaleString(this.$i18n.locale || 'de-DE', { dateStyle: 'medium', timeStyle: 'short' });
    },
  },
};
</script>

<style scoped lang="scss">
.lead-receipts {
  color: $webmag-lighter-grey;
}

.lr-muted {
  // lighter grey: #b9b9b9 on the card grey is only ~3.6:1
  color: $webmag-lighter-grey;
  font-size: 14px;
}

.lr-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 24px;
  margin-bottom: 16px;
}

.lr-count {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.1;
}

.lr-count-number {
  font-size: 30px;
  font-weight: 700;
  color: #fff;
}

.lr-count-label {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: $webmag-light-grey;
}

.lr-note,
.lr-empty {
  padding: 16px;
  background: $bg-header-dark;
  border-radius: 4px;
  color: $webmag-light-grey;
}

.lr-error {
  color: $webmag-red-light;
}

.lr-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.lr-list,
.lr-deliveries,
.lr-hooks {
  list-style: none;
  margin: 0;
  padding: 0;
}

.lr-row {
  margin-bottom: 8px;
  background: $bg-header-dark;
  border-radius: 4px;
  box-shadow: 0 2px 3px 0 rgba(0, 0, 0, 0.16);
}

.lr-row-main {
  display: grid;
  grid-template-columns: 136px minmax(200px, 1fr) 2fr 16px;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding: 16px;
  background: none;
  border: 0;
  color: inherit;
  text-align: left;

  &:hover,
  &:focus-visible {
    background: $bg-header-dark-hover;
    border-radius: 4px;
  }
}

.lr-time {
  color: $webmag-lighter-grey;
  font-size: 14px;
}

.lr-contact {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.lr-contact-email {
  color: #fff;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lr-contact-source {
  font-size: 12px;
  color: $webmag-lighter-grey;
}

.lr-filters {
  display: flex;
  gap: 24px;
}

.lr-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.lr-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 12px;
  background: rgba(255, 255, 255, 0.08);
}

.lr-chip-status {
  font-weight: 600;
}

.lr-chip--sent {
  color: $webmag-green;
}

.lr-chip--pending {
  color: #f0c96b;
}

.lr-chip--failed {
  color: #fff;
  background: rgba(220, 83, 83, 0.55);
}

.lr-chip-n {
  opacity: 0.8;
}

.lr-chevron {
  color: $webmag-light-grey;
}

.lr-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 24px;
  margin-top: 32px;
}

.lr-card {
  padding: 24px;
  background: $bg-header-dark;
  border-radius: 4px;
}

.lr-card-title {
  font-size: 18px;
  color: #fff;
  margin-bottom: 16px;
}

.lr-fields {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
}

.lr-label {
  font-size: 14px;
  margin: 0;
}

.lr-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
  margin-top: 16px;
}

.lr-ok {
  color: $webmag-green;
  font-size: 14px;
}

.lr-error-inline {
  color: $webmag-red-light;
  font-size: 14px;
}

.lr-hook {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.lr-hook-main {
  display: grid;
  grid-template-columns: 8px minmax(0, 1fr);
  align-items: center;
  gap: 4px 8px;
  min-width: 0;

  .lr-muted {
    grid-column: 2;
  }
}

.lr-hook-url {
  color: #fff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lr-hook-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.lr-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: none;
}

.lr-dot--on {
  background: $webmag-green;
}

.lr-dot--off {
  background: $webmag-grey;
}

.lr-hook-form {
  margin-top: 16px;
}

.lr-secret {
  display: flex;
  gap: 8px;
}

.lr-recipe {
  margin-top: 24px;
  font-size: 14px;

  summary {
    cursor: pointer;
    color: $webmag-light-grey;
  }

  pre {
    padding: 16px;
    background: $webmag-dark-grey;
    color: $webmag-lighter-grey;
    border-radius: 4px;
    font-size: 12px;
    white-space: pre-wrap;
  }
}

::v-deep .lr-modal {
  color: #212529;

  .modal-body {
    padding: 16px 24px 24px;
  }

  .modal-title {
    color: #212529;
    font-weight: 600;
  }

  .lr-facts {
    display: grid;
    grid-template-columns: 160px 1fr;
    gap: 4px 16px;
    margin-bottom: 24px;

    dt {
      font-weight: 400;
      color: #6c757d;
    }

    dd {
      margin: 0;
      word-break: break-word;
    }
  }

  .lr-sub {
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #6c757d;
    margin-bottom: 8px;
  }

  .lr-delivery {
    padding: 16px 0;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
  }

  .lr-delivery-top {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .lr-delivery--sent .b-icon {
    color: #2e9e5b;
  }

  .lr-delivery--pending .b-icon {
    color: #b8860b;
  }

  .lr-delivery--failed .b-icon {
    color: $webmag-red-light;
  }

  .lr-resend {
    margin-left: auto;
  }

  .lr-delivery-target {
    font-size: 14px;
    margin-left: 24px;
  }

  .lr-delivery-times {
    display: flex;
    gap: 16px;
    margin-left: 24px;
    font-size: 12px;
    color: #6c757d;
  }

  .lr-raw {
    margin: 4px 0 0 24px;
    font-size: 12px;
    color: #6c757d;

    code {
      display: block;
      margin-top: 4px;
      white-space: pre-wrap;
      color: #495057;
    }
  }

  .lr-delivery-error {
    margin: 4px 0 0 24px;
    font-size: 12px;
    color: #b03a3a;
  }
}
</style>
